import { useMemo } from "react";
import { ImageDisplay } from "src/pages/configurator/components";
import { getFloorPlanImages } from "src/pages/configurator/Configurator.utils";
import { getSummaryPlanDescription } from "src/utils/summary.utils";
import { getSelectedStructuralOptionCodes } from "~components";
import { Css } from "~generated/css";
import { SummaryPrintConfiguration_CheckoutConfigFragment } from "../SummaryPrint.page.generated";
import { PDFPage } from "./PDFPage";
import { PrintHeader } from "./PrintHeader";
import { PrintListItem } from "./PrintListItem";

type FloorPlanReportProps = {
  data: SummaryPrintConfiguration_CheckoutConfigFragment;
  shouldDisplayPricing: boolean;
};

export function FloorPlanReport({ data, shouldDisplayPricing }: FloorPlanReportProps) {
  const { name, status, createdAt, reservedAt, expiresAt, customerEmail, optionGroups, productOffering, isReversed } =
    data ?? {};

  const selectedOptionCodes = useMemo(
    () => getSelectedStructuralOptionCodes(optionGroups, optionGroups.flatMap((og) => og.selectedOption?.id).compact()),
    [optionGroups],
  );

  // Exclude ADU floor plans - they're in the ADU report
  const floorPlanImages = useMemo(() => getFloorPlanImages(productOffering, isReversed), [productOffering, isReversed]);

  return (
    <PDFPage>
      <div css={Css.relative.h100.w100.bgWhite.$}>
        <div css={Css.df.ma.fdc.gap2.p1.pb4.asc.maxContainer.$}>
          <div css={Css.df.fdc.gap2.$}>
            <PrintHeader
              status={status}
              expiredAt={expiresAt}
              createdAt={createdAt}
              reservedAt={reservedAt}
              configurationName={name}
              customerEmail={customerEmail}
            />
            <FloorPlanPDFCard data={data} shouldDisplayPricing={shouldDisplayPricing} isFloorPlanReport />
            {/* While the ImageDisplay component is designed to display multiple images/floor plans and allows the user to toggle between them,
            we want to display each floor as a separate printable section (avoiding page breaks within the image container) */}
            {floorPlanImages.map((floorPlan) => (
              <div key={floorPlan.previewCardName ?? "floor-plan"} css={Css.add("breakInside", "avoid").$}>
                {floorPlanImages.length > 1 && <div css={Css.t12.fw500.mb1.$}>{floorPlan.previewCardName}</div>}
                <div css={Css.df.fdc.mhPx(700).w100.ma.$}>
                  <ImageDisplay images={[floorPlan]} selectedOptionCodes={selectedOptionCodes} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PDFPage>
  );
}

/** Shared with the "Summary" report */
export function FloorPlanPDFCard(props: FloorPlanReportProps & { isFloorPlanReport?: boolean }) {
  const { data, shouldDisplayPricing, isFloorPlanReport = false } = props;
  const { planName, planProgramData } = getSummaryPlanDescription(data);
  return (
    <PrintListItem
      title="Floor Plan"
      // When shown in the "Summary" report, the "plan" portion of the pricing makes sense in the detail breakdown
      shouldDisplayPricing={shouldDisplayPricing && !isFloorPlanReport}
      priceInCents={data.salesPriceInCents}
      items={[{ title: planName }, { title: planProgramData }]}
    />
  );
}
